import * as yup from 'yup';
import {
  requiresStateAndPostalCode,
  isValidStateForCountry,
  validatePostalCodeFormat,
  isUS,
  isUSTerritory,
} from '@agentnet/utils';

const requiredFieldMessage = (fieldName: string): string => `${fieldName} is a required field.`;

export const reportingPartySchema = yup.object().shape({
  partyCategory: yup.string().required(requiredFieldMessage('Reporting Party Category')),

  partyType: yup.string().required(requiredFieldMessage('Reporting Party Type')),

  firstName: yup
    .string()
    .max(40, 'Maximum of 40 characters')
    .when('partyType', {
      is: 'Individual',
      then: (schema) => schema.required(requiredFieldMessage('First Name')),
      otherwise: (schema) => schema.notRequired(),
    }),
  middleName: yup.string().max(40, 'Maximum of 40 characters').notRequired(),
  lastName: yup
    .string()
    .max(40, 'Maximum of 40 characters')
    .when('partyType', {
      is: 'Individual',
      then: (schema) => schema.required(requiredFieldMessage('Last Name')),
      otherwise: (schema) => schema.notRequired(),
    }),
  suffix: yup.string().max(5, 'Maximum of 5 characters').notRequired(),

  entityName: yup
    .string()
    .max(255, 'Maximum of 255 characters')
    .when('partyType', {
      is: 'Entity',
      then: (schema) => schema.required(requiredFieldMessage('Entity Name')),
      otherwise: (schema) => schema.notRequired(),
    }),

  address1: yup.string().max(255, 'Maximum of 255 characters').required(requiredFieldMessage('Address 1')),
  address2: yup.string().max(255, 'Maximum of 255 characters').notRequired(),
  city: yup.string().max(100, 'Maximum of 100 characters').required(requiredFieldMessage('City')),
  usTerritory: yup.string().required(requiredFieldMessage('US/U.S. Territory')),
  state: yup.string().when('usTerritory', {
    is: (usTerritory: string) => requiresStateAndPostalCode(usTerritory),
    then: (schema) =>
      schema
        .trim()
        .required(requiredFieldMessage('State/U.S. Territory'))
        .test('valid-state-for-country', function (value) {
          const { usTerritory } = this.parent;
          if (!value || !usTerritory) return true;

          if (!isValidStateForCountry(value, usTerritory)) {
            return this.createError({
              message: 'Invalid state/province code for the selected country',
            });
          }
          return true;
        }),
    otherwise: (schema) => schema.trim().notRequired(),
  }),
  postalCode: yup
    .string()
    .test('max-length', 'Postal Code cannot exceed 9 characters', function (value) {
      if (!value) return true;
      const trimmedValue = typeof value === 'string' ? value.trim() : value;
      if (trimmedValue === '') return true;
      const { usTerritory } = this.parent;
      // US and US Territories allow ZIP+4 format (10 chars with hyphen)
      if (isUS(usTerritory) || isUSTerritory(usTerritory)) {
        return trimmedValue.length <= 10;
      }
      return trimmedValue.length <= 9;
    })
    .test('format-validation', function (value) {
      if (!value) return true;
      const trimmedValue = typeof value === 'string' ? value.trim() : value;
      if (trimmedValue === '') return true;
      const { usTerritory } = this.parent;

      const error = validatePostalCodeFormat(trimmedValue, usTerritory);
      if (error) {
        return this.createError({ message: error });
      }
      return true;
    })
    .trim()
    .when('usTerritory', {
      is: (usTerritory: string) => requiresStateAndPostalCode(usTerritory),
      then: (schema) => schema.required(requiredFieldMessage('Zip')),
      otherwise: (schema) => schema.notRequired(),
    }),

  closingDate: yup.date().required(requiredFieldMessage('Date of Closing')).typeError('Please enter a valid date'),
});

export default reportingPartySchema;
